import type { Card, ProductType, ShippingSupply } from "@/context/AppContext";
import type {
  EstimatedFulfillmentCost,
  ShippingMethod,
} from "@/types/productOpportunity";

/**
 * Pick a shipping method from card count + product type. The branching follows
 * the spec:
 *   1–4 cards   → PWE / Rigid Envelope
 *   5–25 cards  → Rigid Envelope / Bubble Mailer
 *   26–100      → Bubble Mailer / Small Box
 *   60-card deck → Bubble Mailer / Small Box
 *   sealed      → Small Box
 */
export function pickShippingMethod(
  cardCount: number,
  productType: ProductType,
): ShippingMethod {
  if (productType === "Sealed Bundle") return "Small Box";
  if (
    productType === "Pre-built Deck" ||
    productType === "Deck Core" ||
    productType === "Deck Upgrade Kit"
  ) {
    return cardCount > 30 ? "Bubble Mailer" : "Rigid Envelope";
  }
  if (cardCount <= 4) return "PWE";
  if (cardCount <= 25) return "Rigid Envelope";
  if (cardCount <= 100) return "Bubble Mailer";
  return "Small Box";
}

const POSTAGE_BY_METHOD: Record<ShippingMethod, number> = {
  PWE: 0.78,
  "Rigid Envelope": 1.7,
  "Bubble Mailer": 5.0,
  "Small Box": 7.5,
};

/**
 * Coarse fallback supplies cost when the seller has no shipping-supplies
 * registry to draw from. Each method assumes a sleeve+toploader+stiffener kit
 * appropriate for that mailer class.
 */
const DEFAULT_SUPPLIES_BY_METHOD: Record<ShippingMethod, number> = {
  PWE: 0.35,
  "Rigid Envelope": 0.7,
  "Bubble Mailer": 1.1,
  "Small Box": 1.6,
};

/**
 * Look up a per-method supplies estimate from the seller's ShippingSupply
 * registry, falling back to the coarse default when an entry isn't present.
 * The lookup is name-based (case-insensitive contains) since the registry has
 * no method-tag column; the starter list ships with mailer-style names that
 * naturally match.
 */
function suppliesCostFromRegistry(
  method: ShippingMethod,
  supplies: ShippingSupply[] | undefined,
): number {
  if (!supplies || supplies.length === 0) {
    return DEFAULT_SUPPLIES_BY_METHOD[method];
  }
  const find = (needle: string) =>
    supplies.find((s) => s.name.toLowerCase().includes(needle))?.unitCost ?? 0;
  // Common base kit costs: penny sleeve + toploader for any mailer.
  const baseKit = find("penny sleeve") + find("toploader");
  switch (method) {
    case "PWE":
      // Sleeve + toploader + tape/stiffener; PWE is fragile so still need protection.
      return baseKit + (find("tape") || find("stiffener") || 0);
    case "Rigid Envelope":
      return baseKit + (find("stiffener") || find("card saver") || 0.2);
    case "Bubble Mailer":
      return baseKit + (find("bubble mailer") || 0.6) + (find("team bag") || 0);
    case "Small Box":
      return baseKit + (find("box") || 1.0);
    default:
      return DEFAULT_SUPPLIES_BY_METHOD[method];
  }
}

export interface FulfillmentEstimateInput {
  cards: Card[];
  productType: ProductType;
  /**
   * The seller's full ShippingSupply registry, when available. When omitted
   * the estimator falls back to coarse hard-coded defaults.
   */
  supplies?: ShippingSupply[];
  /** Optional override for cardCount — useful when scoring an empty/incomplete deck. */
  cardCountOverride?: number;
}

export function estimateFulfillment(
  input: FulfillmentEstimateInput,
): EstimatedFulfillmentCost {
  const cardCount =
    input.cardCountOverride ??
    input.cards.reduce((sum, c) => sum + (c.quantity ?? 1), 0);
  const method = pickShippingMethod(cardCount, input.productType);
  const suppliesCost = suppliesCostFromRegistry(method, input.supplies);
  const postageEstimate = POSTAGE_BY_METHOD[method];
  return {
    suppliesCost: round2(suppliesCost),
    postageEstimate: round2(postageEstimate),
    totalFulfillmentCost: round2(suppliesCost + postageEstimate),
    recommendedShippingMethod: method,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
